<script setup lang="ts">
import { useToast } from 'vue-toast-notification'
import { ref } from 'vue'
import { useScreen } from '@/composables/useScreen'

const axios = useAxios()
const { t, messages } = useI18n()
const { user: userState, isUserLoading } = useSession()

const { isMobileView } = useScreen()
const toast = useToast()
const validated = ref(false)
const name = ref('')
const email = ref('')
const theme = ref({})
const message = ref('')
const isOpen = ref(false)
const options = ref([
  { id: 1, label: 'question_about_our_services' },
  { id: 2, label: 'other' }
])

const props = defineProps({
  includeCal: {
    type: Boolean,
    default: false,
    required: false
  }
})


const calendarSelected = ref(false)

function calendarSelect(val: boolean) {
  calendarSelected.value = val
}

onMounted(() => {
  if (!isUserLoading.value) {
    setNameAndEmailFromState()
  } else {
    const unwatchUserState = watch(() => isUserLoading.value, () => {
      setNameAndEmailFromState()
      unwatchUserState()
    })
  }
})

function setNameAndEmailFromState() {
  if (userState.value?.name && userState.value.lastname) {
    name.value = userState.value.name + ' ' + userState.value.lastname
  }

  if (userState.value?.email) {
    email.value = userState.value.email
  }
}

const closeDropdown = () => {
  isOpen.value = false
}

const toggleDropdown = () => {
  isOpen.value = !isOpen.value
}

const selectTheme = (option) => {
  theme.value = option
  isOpen.value = false
}

const emit = defineEmits(['choose'])

function submitForm() {
  validated.value = true
  if (name.value && email.value && theme.value.id && message.value) {
    const requestData = {
      name_surname: name.value,
      email: email.value,
      topic: t(`${theme.value.label}`),
      request_text: message.value
    }
    axios.post('contact-request', requestData).then((result) => {
      if (result.status === 201) {
        toast.success(t('message_successfully_sent'))
      }

      name.value = ''
      email.value = ''
      theme.value = {}
      message.value = ''
      validated.value = false
    })
  }
}
</script>
<template>
  <div class="container font-redhat text-white bg-lightBlue lg:bg-white">
    <div class="bg-lightBlue lg:rounded-[40px] py-18 lg:py-20 lg:flex lg:min-h-[637px]">

      <div :class="isMobileView ? 'mx-6 max-w-[350px]' : 'ml-17.5'">
        <div class="leading-normal font-semibold text-36 lg:text-60">
          {{ $t('contact_us') }}
        </div>
        <div class="mt-6 leading-normal text-md">
          {{ $t('contact_us_description') }}
        </div>
        <div class="mt-12 flex">
          <div>
            <a target="_blank" href="https://www.facebook.com/geriteisininkai.lt"><IconSocialsFacebook class="size-8" /></a>
          </div>
          <div class="ml-6">
            <a target="_blank" href="https://m.me/geriteisininkai.lt"><IconSocialsMessenger class="size-8" /></a>
          </div>
          <div class="ml-6">
            <a target="_blank" href="https://www.linkedin.com/company/geri-teisininkai"><IconSocialsLinkedin class="size-8" /></a>
          </div>
        </div>
      </div>
      <div class="mt-8 lg:mt-0 lg:ml-14 lg:mr-17.5 w-full *:w-full lg:max-w-[470px]">


        <div v-if="props.includeCal" class="flex space-x-3">
          <button
            class="mt-3  w-full text-lightBlue font-semibold text-md h-12 rounded-[10px] transition-all duration-50 active:scale-95 mb-5"
            :class="calendarSelected ? 'bg-white text-lightBlue' : 'bg-transparent text-white hover:bg-white-300 hover:text-lightBlue'"
            @click="calendarSelect(true)">
            {{ $t('remote_meeting') }}

          </button>
          <button
            class="mt-3 w-full text-lightBlue font-semibold text-md h-12 rounded-[10px] transition-all duration-50 active:scale-95 mb-5"
            :class="!calendarSelected ? 'bg-white text-lightBlue' : 'bg-transparent text-white hover:bg-white-300 hover:text-lightBlue'"
            @click="calendarSelect(false)">

            {{ $t('contact_email') }}
          </button>
        </div>

        <div v-if="!calendarSelected || !props.includeCal">
          <div class="">
            <input v-model="name"
              class="placeholder-white bg-lightBlue py-3 px-4 rounded-[10px] w-full focus:outline-none border"
              :class="(validated && !name) ? 'border-red-500' : 'border-white'" :placeholder="$t('name_and_lastname')">
          </div>
          <div class="mt-3">
            <input v-model="email"
              class="placeholder-white bg-lightBlue py-3 px-4 rounded-[10px] w-full focus:outline-none border"
              :class="(validated && !email) ? 'border-red-500' : 'border-white'" :placeholder="$t('email_address')">
          </div>
          <div v-click-outside="closeDropdown" class="relative w-full rounded-[10px] cursor-pointer mt-3 border"
            :class="(validated && !theme.label) ? 'border-red-500' : 'border-white'">
            <div class="flex rounded-[10px] bg-lightBlue py-3 px-4" @click="toggleDropdown">
              {{ $t(theme.label || 'theme') }}
              <div class="ml-auto mr-1 flex items-center">
                <svg width="12" height="7" viewBox="0 0 12 7" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M5.9998 6.9499C5.86647 6.9499 5.74147 6.92907 5.6248 6.8874C5.50814 6.84574 5.3998 6.7749 5.2998 6.6749L0.699804 2.0749C0.516471 1.89157 0.424804 1.65824 0.424805 1.3749C0.424805 1.09157 0.516471 0.858236 0.699805 0.674902C0.883138 0.491569 1.11647 0.399902 1.3998 0.399903C1.68314 0.399903 1.91647 0.491569 2.0998 0.674903L5.9998 4.5749L9.89981 0.674904C10.0831 0.491571 10.3165 0.399904 10.5998 0.399904C10.8831 0.399904 11.1165 0.491571 11.2998 0.674904C11.4831 0.858238 11.5748 1.09157 11.5748 1.3749C11.5748 1.65824 11.4831 1.89157 11.2998 2.0749L6.6998 6.6749C6.5998 6.7749 6.49147 6.84574 6.3748 6.8874C6.25814 6.92907 6.13314 6.9499 5.9998 6.9499Z"
                    fill="white" />
                </svg>
              </div>
            </div>
            <div v-show="isOpen"
              class="absolute top-[50px] w-full bg-lightBlue border-0 shadow-[0_8px_16px_rgba(0,0,0,1)] z-[100]">
              <div v-for="option in options" :key="option.value"
                class="px-4 py-3 hover:bg-white-500 hover:text-darkBlue" @click="selectTheme(option)">
                {{ $t(option.label) }}
              </div>
            </div>
          </div>
          <div class="mt-3">
            <textarea v-model="message"
              class="placeholder-white rounded-[10px] py-[6px] px-[16px] h-[137px] min-h-[60px] max-h-[500px] bg-lightBlue w-full focus:outline-none scrollbar scrollbar--round scrollbar--white border"
              :class="(validated && !message) ? 'border-red-500' : 'border-white'" rows="4"
              :placeholder="$t('your_message')" />
          </div>

          <button
            class="mt-3 bg-white hover:bg-white-300 w-full text-lightBlue font-semibold text-md h-12 rounded-full transition-all duration-50 active:scale-95"
            @click="submitForm()">
            {{ $t('send') }}
          </button>
        </div>
        <div v-else>

          <div class="zoom08 origin-top max-w-[700px]  bg-white rounded-[10px]"
            :class="isMobileView ? 'sm:h-[680px] md:h-[530px]' : 'sm:h-[400px] md:h-[500px] lg:h-[500px] xl:h-[500px] 2xl:h-[580px]'">


            <meeting-calendar :calendar-id="243" :alterColorScheme=true
              @choose="(slot, date) => { emit('choose', slot, date) }" />


          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<style scoped>
.zoom08 {
  zoom: 0.7;
}
</style>
